/**
 * Детектор серии клавиш USB HID-сканера (marking_buffer §6).
 *
 * Сканер «печатает» код очень быстро (≤70 мс между клавишами) и завершает
 * Enter. Человек так не печатает — серия ≥25 символов с таким темпом
 * однозначно означает скан. Чистая логика без DOM — покрыта тестами.
 */

import { isStandardProductBarcode } from './productBarcode';

export const HID_MIN_LENGTH = 25;
export const HID_MAX_INTERVAL_MS = 70;
/** С какого символа серия считается сканом (точка уверенности) */
export const HID_CONFIDENCE_AT = 3;

export type HidScanEvent =
  | { kind: 'burst' } // достигнута точка уверенности «это скан»
  | { kind: 'scan'; code: string } // Enter после подтверждённой серии
  | { kind: 'reset' }; // серия прервана (медленная клавиша)

export class HidScanDetector {
  private buffer = '';
  private lastAt: number | null = null;
  /** true с точки уверенности до Enter/сброса — клавиши надо перехватывать */
  confident = false;

  constructor(
    private now: () => number = () => Date.now(),
    private minLength: number = HID_MIN_LENGTH,
    private maxIntervalMs: number = HID_MAX_INTERVAL_MS,
  ) {}

  private reset(): void {
    this.buffer = '';
    this.lastAt = null;
    this.confident = false;
  }

  /**
   * Обработать keydown. key — KeyboardEvent.key ('a', '5', '>', 'Enter'…).
   * Модификаторы и служебные клавиши (key.length > 1, кроме Enter) —
   * передавать не нужно, слушатель их фильтрует сам.
   */
  keydown(key: string): HidScanEvent | null {
    const t = this.now();

    if (key === 'Enter') {
      if (this.confident && (this.buffer.length >= this.minLength || isStandardProductBarcode(this.buffer))) {
        const code = this.buffer;
        this.reset();
        return { kind: 'scan', code };
      }
      this.reset();
      return null;
    }

    if (key.length !== 1) return null; // модификаторы — не часть кода

    const interval = this.lastAt === null ? null : t - this.lastAt;

    if (interval !== null && interval > this.maxIntervalMs) {
      // Пауза — предыдущая серия была человеческим набором, начинаем заново
      const wasConfident = this.confident;
      this.reset();
      this.buffer = key;
      this.lastAt = t;
      return wasConfident ? { kind: 'reset' } : null;
    }

    this.buffer += key;
    this.lastAt = t;

    if (!this.confident && this.buffer.length >= HID_CONFIDENCE_AT) {
      this.confident = true;
      return { kind: 'burst' };
    }
    return null;
  }
}
